import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SITE } from "@/config";
import { PRODUCT_IMAGES } from "@/config";
import { BuyButton } from "@/components/BuyButton";
import { ProductImage } from "@/components/ProductImage";
import { MaskLine, EASE, Reveal } from "@/lib/motion";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 0.94]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pt-28 md:pt-36"
      data-testid="hero-section"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-16 md:grid-cols-12 md:gap-8 md:px-12 md:pb-24">
        {/* Copy */}
        <div className="md:col-span-6 lg:col-span-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 text-overline"
          >
            The easier way to ask for a Google review
          </motion.p>

          <h1 className="font-display text-5xl font-medium leading-[1.06] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.15}>Turn happy</MaskLine>
            <MaskLine delay={0.27}>customers into</MaskLine>
            <MaskLine delay={0.39}>Google reviews.</MaskLine>
          </h1>

          <Reveal delay={0.5}>
            <p className="mt-7 max-w-md text-lg leading-relaxed text-graphite">
              One simple tap takes your customers straight to your Google review page.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <span className="font-display text-3xl font-semibold tracking-tight text-ink">
                {SITE.price}
              </span>
              <BuyButton testId="buy-now-hero" className="px-8 py-4 text-base">
                Buy now
              </BuyButton>
            </div>

            <div className="mt-6 space-y-1.5">
              <p className="text-sm text-graphite">
                No subscription · No app required · Ready to use
              </p>
              <p className="text-sm font-medium text-ink">
                Configured for your business before shipping.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Product */}
        <div className="md:col-span-6 lg:col-span-6">
          <motion.div
            style={{ y: imgY, scale: imgScale }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: EASE }}
            className="relative"
          >
            <ProductImage
              src={PRODUCT_IMAGES.hero}
              ratio="aspect-[4/5]"
              priority
              alt="Tap Review black glossy NFC Google Review countertop stand on a business counter"
              className="shadow-[0_30px_80px_rgba(0,0,0,0.14)]"
              testId="hero-product-image"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
